import React, { useState, useEffect } from 'react';
import { GiChurch } from 'react-icons/gi';
import { LuSchool, LuArrowRight } from 'react-icons/lu';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../shared/firebase';
import aidedImg from '../../assets/aided_schools.png';
import unaidedImg from '../../assets/unaided_schools.png';

export default function Schools({ activeSection, onSelectSection }) {
  const [aidedCount, setAidedCount] = useState(0);
  const [unaidedCount, setUnaidedCount] = useState(0);

  useEffect(() => {
    let isMounted = true;
    async function fetchStats() {
      try {
        const snap = await getDocs(collection(db, 'schools'));
        let aided = 0;
        let unaided = 0;

        snap.forEach((doc) => {
          const cat = (doc.data().category || 'aided').toLowerCase();
          if (cat.includes('unaided') || cat.includes('un-aided')) {
            unaided++;
          } else {
            aided++;
          }
        });

        if (isMounted) {
          setAidedCount(aided > 0 ? aided : 28);
          setUnaidedCount(unaided > 0 ? unaided : 12);
        }
      } catch (err) {
        if (isMounted) {
          setAidedCount(28);
          setUnaidedCount(12);
        }
      }
    }
    fetchStats();
    return () => { isMounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#5F5A58] font-sans flex flex-col pb-16">
      
      {/* Page Header */}
      <section className="relative bg-gradient-to-b from-[#FAF6F2] via-[#F5EFEE] to-[#E8DDD7] pt-28 sm:pt-32 pb-16 overflow-hidden border-b border-[#E8DDD7]">
        <div className="absolute right-[5%] top-1/2 -translate-y-1/2 pointer-events-none opacity-[0.03] select-none">
          <GiChurch className="w-64 h-64 sm:w-96 sm:h-96 text-[#7B1E3A]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-12 flex flex-col items-center md:items-start text-center md:text-left">
          <div className="w-16 h-1 bg-[#D4A64A] mb-6 rounded-full" />
          <h1 className="font-serif font-bold text-[#7B1E3A] text-[38px] sm:text-[50px] lg:text-[56px] uppercase tracking-[0.03em] leading-[1.05]">
            Diocesan Schools
          </h1>
          <p className="mt-4 max-w-xl text-[#7B6B66] text-[16px] sm:text-[17px] leading-[1.6]">
            From Lower Primary to Higher Secondary, providing foundational academic excellence, moral education, and holistic development across Malabar.
          </p>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="max-w-[1440px] mx-auto px-6 sm:px-8 mt-12 w-full">
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 max-w-5xl mx-auto">
          
          {/* Aided Schools Card */}
          <div className="bg-white rounded-[32px] overflow-hidden border border-[#E8DDD7] shadow-sm hover:shadow-[0_20px_50px_rgba(123,30,58,0.12)] transition-all duration-300 flex flex-col group hover:-translate-y-2">
            <div className="relative h-64 sm:h-72 overflow-hidden bg-[#FAF6F2]">
              <img 
                src={aidedImg} 
                alt="Aided Schools" 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              
              <div className="absolute bottom-6 left-6 right-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30">
                    <LuSchool className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="font-serif font-bold text-white text-3xl tracking-wide">
                      Aided Schools
                    </h2>
                    {aidedCount > 0 && (
                      <span className="text-white/80 text-xs font-bold">
                        {aidedCount}+ Government-Aided Institutions
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-8 flex flex-col flex-grow bg-gradient-to-b from-white to-[#FAF6F2]/30">
              <p className="text-[#5F5A58] text-sm leading-relaxed mb-6 flex-grow">
                Discover the rich heritage and century-long tradition of our aided schools, offering quality education rooted in strong moral values.
              </p>
              <div className="space-y-2.5 w-full">
                {[
                  { name: 'Higher Secondary Schools', route: 'aided-higher-secondary' },
                  { name: 'High Schools', route: 'aided-high-schools' },
                  { name: 'Upper Primary Schools', route: 'aided-up-schools' },
                  { name: 'Lower Primary Schools', route: 'aided-lp-schools' }
                ].map((item, idx) => (
                  <button 
                    key={`aided-${idx}`}
                    onClick={() => onSelectSection(item.route)} 
                    className="flex items-center justify-between w-full px-5 py-3.5 rounded-xl bg-[#FAF6F2] text-[#7B1E3A] font-bold text-[12px] sm:text-[13px] uppercase tracking-wider hover:bg-[#7B1E3A] hover:text-white transition-colors duration-300 group/btn cursor-pointer"
                  >
                    <span>{item.name}</span>
                    <LuArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Unaided Schools Card */}
          <div className="bg-white rounded-[32px] overflow-hidden border border-[#E8DDD7] shadow-sm hover:shadow-[0_20px_50px_rgba(123,30,58,0.12)] transition-all duration-300 flex flex-col group hover:-translate-y-2">
            <div className="relative h-64 sm:h-72 overflow-hidden bg-[#FAF6F2]">
              <img 
                src={unaidedImg} 
                alt="Unaided Schools" 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              
              <div className="absolute bottom-6 left-6 right-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30">
                    <LuSchool className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="font-serif font-bold text-white text-3xl tracking-wide">
                      UnAided Schools
                    </h2>
                    {unaidedCount > 0 && (
                      <span className="text-white/80 text-xs font-bold">
                        {unaidedCount}+ Private Recognised Schools
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-8 flex flex-col flex-grow bg-gradient-to-b from-white to-[#FAF6F2]/30">
              <p className="text-[#5F5A58] text-sm leading-relaxed mb-6 flex-grow">
                Explore the modern facilities and dedicated staff in our English &amp; Malayalam medium unaided schools fostering academic excellence.
              </p>
              
              <div className="space-y-2.5 w-full">
                {[
                  { name: 'Higher Secondary Schools', route: 'unaided-higher-secondary' },
                  { name: 'High Schools', route: 'unaided-high-schools' },
                  { name: 'Upper Primary Schools', route: 'unaided-up-schools' },
                  { name: 'Lower Primary Schools', route: 'unaided-lp-schools' }
                ].map((item, idx) => (
                  <button 
                    key={`unaided-${idx}`}
                    onClick={() => onSelectSection(item.route)} 
                    className="flex items-center justify-between w-full px-5 py-3.5 rounded-xl bg-[#FAF6F2] text-[#7B1E3A] font-bold text-[12px] sm:text-[13px] uppercase tracking-wider hover:bg-[#7B1E3A] hover:text-white transition-colors duration-300 group/btn cursor-pointer"
                  >
                    <span>{item.name}</span>
                    <LuArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                ))}
              </div>
            </div>
          </div>

        </div>
        
        <div className="text-center mt-12 mb-8">
           <button 
             onClick={() => onSelectSection('institutions')}
             className="inline-block text-[#8B8683] font-sans font-bold text-sm tracking-wider uppercase hover:text-[#7B1E3A] transition-colors cursor-pointer"
           >
             &larr; Back to Institutions
           </button>
        </div>

      </section>
    </div>
  );
}
